import { SolrParamsKey } from '~~/shared/solr'

export function useLawWithLegalCommentary() {
  const params = useLawRouteParams()

  // This is fetching all legal commentaries for each paragraph in the law
  const commentIdsByLawIdParams = computed(() => ({ [SolrParamsKey.CommentIdsByLawId]: params.value.lawId }))
  const commentariesQuery = useLawCommentaryQuery('law-commentary', commentIdsByLawIdParams)
  const rootCommentary = computed(() => commentariesQuery.data.value?.response?.docs[0])
  const commentaries = computed(() => rootCommentary.value?.comments?.docs ?? [])

  const commentaryId = computed(() => {
    const { lawFragmentId, paragraph } = params.value

    if (lawFragmentId) {
      for (let id = lawFragmentId; id; id = id.substring(0, id.lastIndexOf('/') || -1)) {
        const match = commentaries.value.find(c => c.lawFragmentId?.toLocaleLowerCase() === id.toLocaleLowerCase())
        if (match) {
          return match.ezMainNodeId
        }

        if (paragraph && isParagraphSegment(id)) {
          const paragraphMatch = commentaries.value.find((c) => {
            const commentaryFragment = c.lawFragmentId
            if (!commentaryFragment) {
              return false
            }

            return commentaryFragment.endsWith(paragraph)
          })

          if (paragraphMatch) {
            return paragraphMatch.ezMainNodeId
          }
        }
      }
    }
    // Fallback: match by lawId if no lawFragmentId is found, relevant for comments for the whole law
    return commentaries.value.find(c => c.lawId === params.value.lawId && !c.lawFragmentId)?.ezMainNodeId
  })

  // This is fetching the `nodeType: legal_commentary` document for the commentary itself
  const commentIdParams = computed(() => ({ [SolrParamsKey.CommentByEzMainNodeId]: commentaryId.value?.toString() }))
  const legalCommentaryQuery = useLawCommentaryQuery('law-commentary', commentIdParams)
  const legalCommentary = computed(() => legalCommentaryQuery.data.value?.response?.docs[0])

  // We're using useFetchBookEdition composable to fetch the book edition for the commentary
  const bookEditionId = computed(() => legalCommentary.value?.editionId ?? commentaries.value.find(c => c.editionId)?.editionId)
  const bookQuery = useFetchBookEditions(bookEditionId)
  const book = computed(() => bookQuery.data.value?.editions.find(e => e.id === bookEditionId.value))

  // This is fetching the `nodeType: law` document for the law itself
  const lawIdParams = computed(() => ({ [SolrParamsKey.LawId]: params.value.lawId }))
  const lawQuery = useLawCommentaryQuery('law-commentary', lawIdParams)
  const law = computed(() => lawQuery.data.value?.response?.docs[0])

  return {
    legalCommentaryQuery,
    commentariesQuery,
    bookQuery,
    lawQuery,
    legalCommentary,
    rootCommentary,
    commentaries,
    book,
    law,
  }
}
